import { useNavigate } from "react-router-dom";
import { Check, Truck, Lock, Clock, ShoppingBag } from "lucide-react";
import { useProduct, useTestimonials } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { euro, imgUrl } from "@/lib/api";
import { SHOP } from "@/lib/shop";
import { useSeo } from "@/lib/seo";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { LazyAutoplayVideo } from "@/components/site/LazyAutoplayVideo";
import { ShopRating, useShopReviews, ReviewCard } from "@/components/site/ShopReviews";
import { PaymentLogos } from "@/components/site/Trust";
import { Faq } from "@/components/site/Faq";
import { StickyBuyBar } from "@/components/site/StickyBuyBar";

const SLUG = "traitement-3-extreme-pouss";
const STEPS = [
  { n: "1", t: "Appliquez", d: "Quelques gouttes d'Extrême Repouss directement sur le cuir chevelu, raie par raie." },
  { n: "2", t: "Massez", d: "Une à deux minutes du bout des doigts pour bien répartir la lotion sur les zones fragiles." },
  { n: "3", t: "Soyez régulière", d: "Chaque jour : avec 3 flacons, vous suivez la routine dans la durée, sans interruption." },
];
const FAQ = [
  { q: "Combien de temps dure le traitement ?", a: "Le traitement contient 3 flacons de 125 ml d'Extrême Repouss, pour suivre une routine régulière sur plusieurs semaines. Les premiers effets sont généralement observés après 3 à 4 semaines d'utilisation régulière ; les résultats varient selon chaque personne." },
  { q: "Pour quels cheveux ?", a: "Pour tous les types de cheveux afro, crépus, frisés, défrisés ou bouclés, y compris les tempes et zones clairsemées. Pour les cheveux très fins, il existe une version Cheveux Fins." },
  { q: "Combien coûte la livraison ?", a: "La livraison en Point Relais ou Locker est offerte partout en France métropolitaine. Les autres options (domicile, DOM-TOM, Europe, international) sont calculées à l'étape de paiement." },
  { q: "Le paiement est-il sécurisé ?", a: "Oui : carte bancaire avec 3D Secure ou PayPal. Vos données bancaires ne passent jamais par notre site." },
];

function Hero({ p, onBuy, onAdd }) {
  return (
    <section data-testid="offer-hero" className="mx-auto grid max-w-6xl gap-8 px-5 pb-14 md:grid-cols-2 md:items-start md:gap-14 md:px-10 md:pt-40 pt-36">
      <div className="relative overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--ink-2)]">
        <img src={imgUrl(p.image)} alt={p.name} data-testid="offer-product-image" className="aspect-square w-full object-cover" />
        <span className="absolute left-4 top-4 rounded-full bg-[var(--gold)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--ink)]">3 flacons · 3 × 125 ml</span>
      </div>
      <div>
        <ShopRating testid="offer-rating" />
        <div data-testid="offer-quick-buy" className="mt-4 flex items-center justify-between gap-3 md:hidden">
          <span className="font-display text-3xl text-[var(--gold-soft)]">{euro(p.price)}</span>
          <button type="button" onClick={onBuy} data-testid="offer-quick-buy-btn" className="h-12 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ink)] active:scale-95">Commander</button>
        </div>
        <h1 data-testid="offer-title" className="mt-4 font-display text-4xl leading-[1.05] lg:text-5xl">Chute, casse, tempes clairsemées ? <span className="italic gold-text">Le traitement Extrême Repouss.</span></h1>
        <p className="mt-5 text-sm leading-relaxed text-[var(--parchment)]/90 md:text-base">{p.description}</p>
        <ul className="mt-6 space-y-2 text-sm md:text-base">
          {(p.benefits || []).slice(0, 4).map((b) => <li key={b} className="flex gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold)]" />{b}</li>)}
        </ul>
        <BuyBox p={p} onBuy={onBuy} onAdd={onAdd} />
      </div>
    </section>
  );
}

function BuyBox({ p, onBuy, onAdd, id = "main-buy" }) {
  return (
    <div id={id} data-testid={`offer-buy-box-${id}`} className="mt-8 rounded-sm border border-[var(--gold)]/50 bg-[var(--ink-2)] p-5">
      <div className="flex items-end justify-between gap-4">
        <p data-testid={`offer-price-${id}`} className="font-display text-4xl text-[var(--gold-soft)]">{euro(p.price)}</p>
        <p className="text-right text-xs text-[var(--parchment)]/85">soit {euro(p.price / 3)} le flacon</p>
      </div>
      <button type="button" onClick={onBuy} data-testid={`offer-buy-now-${id}`} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink)] transition-transform hover:scale-[1.01] active:scale-95">
        <ShoppingBag className="h-5 w-5" /> Commander maintenant
      </button>
      <button type="button" onClick={onAdd} data-testid={`offer-add-cart-${id}`} className="mt-3 h-11 w-full rounded-full border border-[var(--line)] text-xs uppercase tracking-[0.16em] transition-colors hover:border-[var(--gold)]">Ajouter au panier</button>
      <ul className="mt-5 grid gap-2 text-xs text-[var(--parchment)]/90 sm:grid-cols-3">
        <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-[var(--gold)]" />Point Relais offert en France</li>
        <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-[var(--gold)]" />{SHOP.shipDelay}</li>
        <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-[var(--gold)]" />Paiement sécurisé</li>
      </ul>
      <PaymentLogos className="mt-4" testid={`offer-payment-logos-${id}`} />
    </div>
  );
}

function Results() {
  const { data } = useTestimonials();
  const list = (Array.isArray(data) ? data : []).slice(0, 3);
  return (
    <section data-testid="offer-results" className="bg-[var(--ink-2)] px-5 py-16 md:px-10 md:py-24">
      <div className="mx-auto max-w-6xl">
        <p className="eyebrow mb-3">Photos réelles de clientes</p>
        <h2 className="font-display text-3xl leading-tight md:text-5xl">Avant / après <span className="italic gold-text">Ozentya</span></h2>
        <p className="mt-3 text-sm text-[var(--parchment)]/85">Glissez le curseur. Résultats individuels, variables selon chaque personne et la régularité de la routine.</p>
        <div className="mt-10 grid gap-10 md:grid-cols-3">
          {list.map((t, i) => (
            <figure key={t.id || i}>
              <BeforeAfter before={t.before_image} after={t.after_image} alt={t.name} testid={`offer-before-after-${i}`} />
              <figcaption className="mt-3 text-sm text-[var(--parchment)]/90">{t.name}{t.duration ? ` · ${t.duration}` : ""}{t.product ? ` · ${t.product}` : ""}</figcaption>
            </figure>
          ))}
        </div>
        <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2">
          {["/results/video-avant-apres", "/results/video-avant-apres-2"].map((v, i) => (
            <LazyAutoplayVideo key={v} src={`${v}.mp4`} poster={`${v}.webp`} data-testid={`offer-video-${i}`}
              aria-label="Vidéo avant / après d'une cliente Ozentya" className="mx-auto aspect-[9/16] w-full max-w-[320px] rounded-sm border border-[var(--line)] bg-black object-cover" />
          ))}
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section data-testid="offer-steps" className="mx-auto max-w-6xl px-5 py-16 md:px-10 md:py-24">
      <p className="eyebrow mb-3">Mode d'emploi</p>
      <h2 className="font-display text-3xl leading-tight md:text-5xl">3 gestes, <span className="italic gold-text">2 minutes par jour</span></h2>
      <ol className="mt-10 grid gap-6 md:grid-cols-3">
        {STEPS.map((s) => (
          <li key={s.n} data-testid={`offer-step-${s.n}`} className="rounded-sm border border-[var(--line)] p-6">
            <span className="font-display text-5xl text-[var(--gold)]">{s.n}</span>
            <p className="mt-3 text-lg font-semibold">{s.t}</p>
            <p className="mt-2 text-sm leading-relaxed text-[var(--parchment)]/85">{s.d}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Reviews() {
  const { data } = useShopReviews({ featured: true, per_page: 3 });
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return null;
  return (
    <section data-testid="offer-reviews" className="mx-auto max-w-6xl px-5 pb-8 md:px-10">
      <h2 className="font-display text-3xl leading-tight md:text-5xl">Elles en parlent</h2>
      <ShopRating testid="offer-reviews-rating" className="mt-3" />
      <ul className="mt-8 grid gap-6 md:grid-cols-3">{items.map((r, i) => <ReviewCard key={`${r.date}-${i}`} r={r} testid={`offer-review-${i}`} />)}</ul>
    </section>
  );
}

export default function OfferPage() {
  const { data: p } = useProduct(SLUG);
  const { add, setOpen } = useCart();
  const nav = useNavigate();
  useSeo({ title: "Traitement anti-chute 3 Extrême Repouss — Ozentya", description: "3 flacons Extrême Repouss pour cheveux afro fragilisés : 45 €, livraison offerte en Point Relais en France.", path: "/offre", noindex: true });
  if (!p) return <div data-testid="offer-loading" className="min-h-[60vh]" />;
  const onAdd = () => add(p, 1);
  const onBuy = () => { add(p, 1); setOpen(false); nav("/commande"); };
  return (
    <div data-testid="offer-page" className="pb-24 lg:pb-0">
      <Hero p={p} onBuy={onBuy} onAdd={onAdd} />
      <Results />
      <Steps />
      <Reviews />
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <Faq items={FAQ} id="offer-faq" />
        <div className="mx-auto max-w-xl pb-20"><BuyBox p={p} onBuy={onBuy} onAdd={onAdd} id="final-buy" /></div>
      </div>
      <StickyBuyBar p={p} targetId="main-buy" onAdd={onAdd} />
    </div>
  );
}
